/**
 * Modal component for uploading scorecard images.
 *
 * Redesigned with The Linescore design system tokens.
 * Two-step wizard with post-upload celebration screen.
 *
 * Step 1: File select (drag/drop), game selector, caption, attended, MVP, visibility
 * Step 2: "Who are you rooting for?" team selector, scoring metadata
 * Celebration: Confetti, share buttons, enrichment nudge
 */
import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { SignInButton, useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import * as pdfjsLib from 'pdfjs-dist';
import { useUploadScorecard, useCheckExistingScorecard } from '../../hooks/useScorecards';
import { useTrackEvent, AnalyticsEventType } from '../../hooks/useAnalytics';
import { useScoringDefaults } from '../../hooks/useScoringDefaults';
import { useUserProfile } from '../../hooks/useUserProfile';
import { mlbApi } from '../../services/api';
import { STALE_TIMES } from '../../types/statcast';
import { GameSelector } from './GameSelector';
import { MvpSelector } from './MvpSelector';
import { ScorecardMetadataFields } from './ScorecardMetadataFields';
import { CelebrationScreen } from './CelebrationScreen';
import type { ScorecardMetadataValues } from './ScorecardMetadataFields';
import type { GamePlayersResponse } from '../../types/boxscore';
import type { NewlyEarnedBadge } from '../../services/api';
import { getTeamName } from '../../utils/constants';
import { teamColors } from '../../data/teamColors';
import { BadgeCelebration } from '../badges/BadgeCelebration';
import { useFeatureFlagWithLoading } from '../../contexts/FeatureFlagContext';

type UploadStep = 'select' | 'metadata' | 'celebration';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

interface UploadScorecardModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Optional pre-selected game ID */
  gameId?: number;
  /** Optional game context for display */
  gameContext?: {
    date: string;
    homeTeam: string;
    awayTeam: string;
  };
}

const ACCEPTED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
  'application/pdf',
];
const MAX_FILE_SIZE_MB = 30;
const MAX_CAPTION_LENGTH = 500;

export function UploadScorecardModal({
  isOpen,
  onClose,
  gameId,
  gameContext,
}: UploadScorecardModalProps) {
  const uploadMutation = useUploadScorecard();
  const trackEvent = useTrackEvent();
  const { isSignedIn } = useAuth();
  const { data: scoringDefaults } = useScoringDefaults();
  const { data: userProfile } = useUserProfile();
  const { enabled: showBadgesFlag } = useFeatureFlagWithLoading('show_badges');

  // Form state
  const [currentStep, setCurrentStep] = useState<UploadStep>('select');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [pdfPages, setPdfPages] = useState<{ url: string; width: number; height: number }[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedGameId, setSelectedGameId] = useState<number | null>(gameId ?? null);
  const [isPublic, setIsPublic] = useState(true);
  const [attendedInPerson, setAttendedInPerson] = useState(false);
  const [caption, setCaption] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rotation, setRotation] = useState<0 | 90 | 180 | 270>(0);
  const [pageRotations, setPageRotations] = useState<(0 | 90 | 180 | 270)[]>([]);
  const [orientation, setOrientation] = useState<'auto' | 'landscape' | 'portrait'>('auto');
  const [isPdfLoading, setIsPdfLoading] = useState(false);

  // Scoring metadata state (pre-filled from user defaults)
  const [viewingMethod, setViewingMethod] = useState<string | null>(null);
  const [scoringTool, setScoringTool] = useState<string | null>(null);
  const [gamePrep, setGamePrep] = useState<string | null>(null);
  const [scoringStyle, setScoringStyle] = useState<string | null>(null);
  const [scorebookType, setScorebookType] = useState<string | null>(null);
  const [scorebookSubtype, setScorebookSubtype] = useState<string | null>(null);

  // Per-scorecard rooting choice
  const [selectedGameOutcome, setSelectedGameOutcome] = useState<
    'home_fan' | 'away_fan' | 'neutral'
  >('neutral');

  // MVP selection state
  const [mvpPlayerId, setMvpPlayerId] = useState<number | null>(null);
  const [mvpPlayerName, setMvpPlayerName] = useState<string | null>(null);

  // Celebration state
  const [uploadedScorecardId, setUploadedScorecardId] = useState<number | null>(null);
  const [showBadgeCelebration, setShowBadgeCelebration] = useState(false);
  const [earnedBadges, setEarnedBadges] = useState<NewlyEarnedBadge[]>([]);
  const [pendingAction, setPendingAction] = useState<'close' | 'upload-another' | null>(null);

  // Overwrite confirmation state
  const [showOverwriteConfirm, setShowOverwriteConfirm] = useState(false);
  const [existingThumbnailUrl, setExistingThumbnailUrl] = useState<string | null>(null);
  const checkExistingMutation = useCheckExistingScorecard();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Fetch game players for MVP selection
  const { data: gamePlayers, isLoading: playersLoading } = useQuery<GamePlayersResponse>({
    queryKey: ['game-players', selectedGameId],
    queryFn: () => mlbApi.getGamePlayers(selectedGameId!),
    enabled: !!selectedGameId && isOpen,
    staleTime: STALE_TIMES.CAREER_STATS,
  });

  // Compute default rooting choice based on user's favorite team
  const defaultGameOutcome = useMemo((): 'home_fan' | 'away_fan' | 'neutral' => {
    if (!gamePlayers || !userProfile?.favorite_team_code) return 'neutral';
    const favoriteTeam = userProfile.favorite_team_code.toLowerCase();
    const homeTeam = gamePlayers.home_team_code.toLowerCase();
    const awayTeam = gamePlayers.away_team_code.toLowerCase();
    if (favoriteTeam === homeTeam) return 'home_fan';
    if (favoriteTeam === awayTeam) return 'away_fan';
    return 'neutral';
  }, [gamePlayers, userProfile?.favorite_team_code]);

  useEffect(() => {
    setSelectedGameOutcome(defaultGameOutcome);
  }, [defaultGameOutcome]);

  useEffect(() => {
    if (isOpen && gameId !== undefined) {
      setSelectedGameId(gameId);
    }
  }, [isOpen, gameId]);

  useEffect(() => {
    if (isOpen && scoringDefaults?.defaults && isSignedIn) {
      const defaults = scoringDefaults.defaults;
      setViewingMethod(defaults.viewing_method ?? null);
      setScoringTool(defaults.scoring_tool ?? null);
      setGamePrep(defaults.game_prep ?? null);
      setScoringStyle(defaults.scoring_style ?? null);
      setScorebookType(defaults.scorebook_type ?? null);
      setScorebookSubtype(defaults.scorebook_subtype ?? null);
    }
  }, [isOpen, scoringDefaults, isSignedIn]);

  // Keyboard handling (Escape to close)
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const resetForm = useCallback(() => {
    setCurrentStep('select');
    setSelectedFile(null);
    setPreviewUrl(null);
    setPdfPages([]);
    setCurrentPage(0);
    setSelectedGameId(gameId ?? null);
    setIsPublic(true);
    setAttendedInPerson(false);
    setCaption('');
    setError(null);
    setRotation(0);
    setPageRotations([]);
    setOrientation('auto');
    setIsPdfLoading(false);
    // Re-apply scoring defaults (the useEffect won't re-fire since isOpen/scoringDefaults/isSignedIn haven't changed)
    const defaults = scoringDefaults?.defaults;
    setViewingMethod(defaults?.viewing_method ?? null);
    setScoringTool(defaults?.scoring_tool ?? null);
    setGamePrep(defaults?.game_prep ?? null);
    setScoringStyle(defaults?.scoring_style ?? null);
    setScorebookType(defaults?.scorebook_type ?? null);
    setScorebookSubtype(defaults?.scorebook_subtype ?? null);
    setSelectedGameOutcome('neutral');
    setMvpPlayerId(null);
    setMvpPlayerName(null);
    setUploadedScorecardId(null);
    setShowBadgeCelebration(false);
    setEarnedBadges([]);
    setPendingAction(null);
    setShowOverwriteConfirm(false);
    setExistingThumbnailUrl(null);
  }, [gameId, scoringDefaults]);

  // File handling
  const handleFileSelect = useCallback(async (file: File) => {
    setError(null);
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError('Unsupported file type. Please use JPEG, PNG, WebP, HEIC, or PDF.');
      return;
    }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setError(`File too large. Maximum size is ${MAX_FILE_SIZE_MB}MB.`);
      return;
    }

    setSelectedFile(file);

    if (
      file.type.startsWith('image/') &&
      file.type !== 'image/heic' &&
      file.type !== 'image/heif'
    ) {
      const reader = new FileReader();
      reader.onload = (e) => setPreviewUrl(e.target?.result as string);
      reader.readAsDataURL(file);
    } else if (file.type === 'application/pdf') {
      setIsPdfLoading(true);
      setPdfPages([]);
      setCurrentPage(0);
      try {
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        const pages: { url: string; width: number; height: number }[] = [];
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale: 1.5 });
          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          const context = canvas.getContext('2d');
          if (context) {
            await page.render({ canvasContext: context, viewport, canvas }).promise;
            pages.push({
              url: canvas.toDataURL('image/png'),
              width: viewport.width,
              height: viewport.height,
            });
          }
        }
        setPdfPages(pages);
        setPageRotations(new Array(pages.length).fill(0) as (0 | 90 | 180 | 270)[]);
        if (pages.length > 0) setPreviewUrl(pages[0].url);
      } catch (err) {
        console.error('Failed to render PDF preview:', err);
        setPreviewUrl(null);
        setPdfPages([]);
      } finally {
        setIsPdfLoading(false);
      }
    } else {
      setPreviewUrl(null);
    }
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      if (e.dataTransfer.files.length > 0) void handleFileSelect(e.dataTransfer.files[0]);
    },
    [handleFileSelect]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) void handleFileSelect(e.target.files[0]);
    },
    [handleFileSelect]
  );

  // Build FormData and submit upload
  const performUpload = async (replaceExisting: boolean) => {
    if (!selectedFile || !selectedGameId) return;
    setError(null);

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('game_id', selectedGameId.toString());
    formData.append('is_public', isPublic.toString());
    formData.append('attended_in_person', attendedInPerson.toString());
    if (caption.trim()) formData.append('caption', caption.trim().substring(0, MAX_CAPTION_LENGTH));
    if (viewingMethod) formData.append('viewing_method', viewingMethod);
    if (scoringTool) formData.append('scoring_tool', scoringTool);
    if (gamePrep) formData.append('game_prep', gamePrep);
    if (scoringStyle) formData.append('scoring_style', scoringStyle);
    if (scorebookType) formData.append('scorebook_type', scorebookType);
    if (scorebookSubtype) formData.append('scorebook_subtype', scorebookSubtype);
    formData.append('game_outcome', selectedGameOutcome);
    if (mvpPlayerId && mvpPlayerName) {
      formData.append('mvp_player_mlb_id', mvpPlayerId.toString());
      formData.append('mvp_player_name', mvpPlayerName);
    }
    if (pdfPages.length > 0 && pageRotations.length > 0) {
      formData.append('page_rotations', JSON.stringify(pageRotations));
    } else if (rotation !== 0) {
      formData.append('rotation', rotation.toString());
    }
    formData.append('orientation', orientation);
    if (replaceExisting) {
      formData.append('replace_existing', 'true');
    }

    trackEvent({
      eventType: AnalyticsEventType.UPLOAD_STARTED,
      targetType: 'scorecard',
      eventData: { game_id: selectedGameId, is_public: isPublic, has_caption: !!caption.trim() },
    });

    try {
      const result = await uploadMutation.mutateAsync(formData);
      if (result.status === 'duplicate') {
        setError('This scorecard appears to already be uploaded.');
        return;
      }
      // Show celebration
      setUploadedScorecardId(result.scorecard_id);
      setEarnedBadges(result.newly_earned_badges || []);
      setCurrentStep('celebration');
      trackEvent({
        eventType: AnalyticsEventType.UPLOAD_COMPLETED,
        targetType: 'scorecard',
        targetId: result.scorecard_id ?? undefined,
        eventData: {
          game_id: selectedGameId,
          badges_earned: (result.newly_earned_badges || []).length,
        },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
    }
  };

  // Upload handler — checks for existing scorecard first, then uploads
  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file to upload.');
      return;
    }
    if (!selectedGameId) {
      setError('Please select a game for this scorecard.');
      return;
    }
    setError(null);

    // Check if user already has a scorecard for this game
    try {
      const existingCheck = await checkExistingMutation.mutateAsync(selectedGameId);
      if (existingCheck.has_existing) {
        setExistingThumbnailUrl(existingCheck.existing_thumbnail_url);
        setShowOverwriteConfirm(true);
        return;
      }
    } catch {
      // If check fails, proceed with upload anyway (non-blocking)
    }

    await performUpload(false);
  };

  const handleOverwriteConfirm = async () => {
    setShowOverwriteConfirm(false);
    await performUpload(true);
  };

  const handleOverwriteCancel = () => {
    setShowOverwriteConfirm(false);
    setExistingThumbnailUrl(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleUploadAnother = () => {
    resetForm();
  };

  const handleCelebrationClose = () => {
    // If badges were earned and flag is on, show badge celebration
    if (showBadgesFlag && earnedBadges.length > 0) {
      setPendingAction('close');
      setShowBadgeCelebration(true);
    } else {
      handleClose();
    }
  };

  const handleCelebrationUploadAnother = () => {
    // If badges were earned and flag is on, show badge celebration
    if (showBadgesFlag && earnedBadges.length > 0) {
      setPendingAction('upload-another');
      setShowBadgeCelebration(true);
    } else {
      handleUploadAnother();
    }
  };

  const handleBadgeCelebrationClose = () => {
    setShowBadgeCelebration(false);
    // Execute the pending action
    if (pendingAction === 'close') {
      handleClose();
    } else if (pendingAction === 'upload-another') {
      handleUploadAnother();
    }
  };

  // Build game label for celebration screen
  const gameLabel = useMemo(() => {
    if (gameContext) return `${gameContext.awayTeam} @ ${gameContext.homeTeam}`;
    if (gamePlayers) {
      const away =
        getTeamName(gamePlayers.away_team_code) || gamePlayers.away_team_code.toUpperCase();
      const home =
        getTeamName(gamePlayers.home_team_code) || gamePlayers.home_team_code.toUpperCase();
      return `${away} @ ${home}`;
    }
    return 'Your Game';
  }, [gameContext, gamePlayers]);

  const gameDetail = useMemo(() => {
    if (gameContext) return gameContext.date;
    return '';
  }, [gameContext]);

  // Check if metadata is sparse (for enrichment nudge)
  const isSparseMetadata = !caption && !mvpPlayerId && !viewingMethod && !scoringTool;

  // Don't render if not open
  if (!isOpen) return null;

  // Show sign-in prompt for non-authenticated users
  if (!isSignedIn) {
    return (
      <div
        className="fixed inset-0 bg-ink/85 flex items-center justify-center z-50 backdrop-blur-[4px]"
        onClick={handleClose}
        role="dialog"
        aria-modal="true"
        aria-labelledby="signin-modal-title"
      >
        <div
          className="bg-cream rounded-lg w-[420px] max-h-[85vh] overflow-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-6 py-5 border-b border-sand flex justify-between items-center">
            <h2 id="signin-modal-title" className="font-display text-[20px] font-black text-ink">
              Sign In Required
            </h2>
            <button
              onClick={handleClose}
              className="text-warm-gray hover:text-ink text-[20px] px-2 py-1"
              aria-label="Close modal"
            >
              &#10005;
            </button>
          </div>
          <div className="text-center py-8 px-6">
            <div className="text-[40px] mb-4 opacity-50">&#128203;</div>
            <h3 className="font-display text-[18px] font-black text-ink mb-2">Sign in to upload</h3>
            <p className="font-serif text-[13px] text-espresso leading-[1.6] mb-6">
              Create an account or sign in to upload your scorecards. You'll be able to manage,
              edit, and share your collection.
            </p>
            <SignInButton mode="modal">
              <button className="bg-ink text-linen font-sans text-[13px] font-bold px-6 py-3 rounded hover:opacity-90 transition-opacity">
                Sign In
              </button>
            </SignInButton>
          </div>
        </div>
      </div>
    );
  }

  // Metadata values for ScorecardMetadataFields
  const metadataValues: ScorecardMetadataValues = {
    viewingMethod,
    scoringTool,
    gamePrep,
    scoringStyle,
    scorebookType,
    scorebookSubtype,
  };

  const handleMetadataChange = (field: keyof ScorecardMetadataValues, value: string | null) => {
    switch (field) {
      case 'viewingMethod':
        setViewingMethod(value);
        break;
      case 'scoringTool':
        setScoringTool(value);
        break;
      case 'gamePrep':
        setGamePrep(value);
        break;
      case 'scoringStyle':
        setScoringStyle(value);
        break;
      case 'scorebookType':
        setScorebookType(value);
        break;
      case 'scorebookSubtype':
        setScorebookSubtype(value);
        break;
    }
  };

  // Rooting selector button helper
  const rootingButton = (
    outcome: 'away_fan' | 'neutral' | 'home_fan',
    label: string,
    teamCode?: string
  ) => {
    const isSelected = selectedGameOutcome === outcome;
    const color = teamCode ? teamColors[teamCode.toUpperCase()] || '#7A6548' : '#7A6548';

    return (
      <button
        type="button"
        onClick={() => setSelectedGameOutcome(outcome)}
        className={`flex items-center justify-center px-3 py-2.5 rounded border-2 font-sans text-[13px] font-bold transition-all ${
          isSelected ? '' : 'border-sand text-warm-gray hover:border-sand-border'
        }`}
        style={
          isSelected
            ? {
                borderColor: color,
                color: color,
                backgroundColor: `${color}15`,
              }
            : {}
        }
      >
        {label}
      </button>
    );
  };

  return (
    <div
      className="fixed inset-0 bg-ink/85 flex items-center justify-center z-50 backdrop-blur-[4px]"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="upload-modal-title"
    >
      <div
        ref={modalRef}
        className="bg-cream rounded-lg w-[520px] max-h-[85vh] overflow-auto relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Celebration screen (replaces everything) */}
        {currentStep === 'celebration' && !showBadgeCelebration && (
          <CelebrationScreen
            scorecardId={uploadedScorecardId}
            previewUrl={previewUrl}
            gameLabel={gameLabel}
            gameDetail={gameDetail}
            isSparse={isSparseMetadata}
            onClose={handleCelebrationClose}
            onUploadAnother={handleCelebrationUploadAnother}
            isGamedayContext={!!gameId}
          />
        )}

        {/* Upload flow (step 1 + step 2) */}
        {currentStep !== 'celebration' && (
          <>
            {/* Header — contextual variant when game is pre-selected */}
            {gameContext && gameId ? (
              <div className="bg-ink px-6 py-4 rounded-t-lg">
                <div className="font-sans text-[9px] tracking-[2px] text-gold uppercase mb-1.5">
                  Adding scorecard for
                </div>
                <h2
                  id="upload-modal-title"
                  className="font-display text-[20px] font-black text-linen"
                >
                  {gameContext.awayTeam} @ {gameContext.homeTeam}
                </h2>
                <div className="font-sans text-[12px] text-warm-gray-light mt-0.5">
                  {gameContext.date}
                </div>
              </div>
            ) : (
              <div className="px-6 py-5 border-b border-sand flex justify-between items-center">
                <div>
                  <h2
                    id="upload-modal-title"
                    className="font-display text-[20px] font-black text-ink"
                  >
                    Add to the Archive
                  </h2>
                  <p className="font-sans text-[12px] text-warm-gray mt-0.5">
                    {currentStep === 'select' && 'Upload your scorecard'}
                    {currentStep === 'metadata' && 'Scoring details'}
                  </p>
                </div>
                <button
                  onClick={handleClose}
                  className="text-warm-gray hover:text-ink text-[20px] px-2 py-1 transition-colors"
                  aria-label="Close modal"
                >
                  &#10005;
                </button>
              </div>
            )}

            {/* Contextual header close button */}
            {gameContext && gameId && (
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 text-warm-gray-light hover:text-linen text-[20px] px-2 py-1 transition-colors"
                aria-label="Close modal"
              >
                &#10005;
              </button>
            )}

            {/* Bulk upload link — only on step 1, not contextual upload */}
            {currentStep === 'select' && !gameId && (
              <div className="px-6 py-3 flex items-center justify-center gap-2 bg-cream-light border-b border-sand">
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none" className="opacity-60">
                  <rect
                    x="2"
                    y="3"
                    width="16"
                    height="14"
                    rx="2"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    className="text-gold-text"
                  />
                  <line
                    x1="2"
                    y1="7"
                    x2="18"
                    y2="7"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    className="text-gold-text"
                  />
                  <line
                    x1="7"
                    y1="7"
                    x2="7"
                    y2="17"
                    stroke="currentColor"
                    strokeWidth="1"
                    className="text-gold-text"
                  />
                  <line
                    x1="13"
                    y1="7"
                    x2="13"
                    y2="17"
                    stroke="currentColor"
                    strokeWidth="1"
                    className="text-gold-text"
                  />
                </svg>
                <Link
                  to="/scorecards/bulk-upload"
                  onClick={onClose}
                  className="font-sans text-[13px] font-semibold text-gold-text underline decoration-dotted underline-offset-[3px] hover:text-gold"
                >
                  Have a stack? Try bulk upload
                </Link>
                <span className="font-sans text-[12px] text-warm-gray-light">&rarr;</span>
              </div>
            )}

            {/* Step indicator */}
            <div className="flex justify-center gap-2 pt-4 px-6" aria-hidden="true">
              <div
                className={`h-1.5 w-8 rounded-full transition-colors ${
                  currentStep === 'select' ? 'bg-gold' : 'bg-parchment'
                }`}
              />
              <div
                className={`h-1.5 w-8 rounded-full transition-colors ${
                  currentStep === 'metadata' ? 'bg-gold' : 'bg-parchment'
                }`}
              />
            </div>
            <span className="sr-only" aria-live="polite">
              {currentStep === 'select'
                ? 'Step 1 of 2: Select file and game'
                : 'Step 2 of 2: Scoring details'}
            </span>

            {/* Body */}
            <div className="px-6 py-5">
              {/* Error display */}
              {error && (
                <div
                  className="bg-error-bg border border-error-border rounded p-3 mb-4"
                  role="alert"
                >
                  <p className="font-sans text-[13px] text-error">{error}</p>
                </div>
              )}

              {/* ===== STEP 1: File + Game + Details ===== */}
              {/* Keep step 1 mounted (hidden) so GameSelector retains internal date state when user navigates Back from step 2 */}
              <div className={`space-y-4 ${currentStep !== 'select' ? 'hidden' : ''}`}>
                {/* File drop zone */}
                <div
                  className={`border-2 border-dashed rounded-[6px] text-center cursor-pointer transition-colors focus-visible:ring-2 focus-visible:ring-ink focus-visible:outline-none ${
                    isDragging
                      ? 'border-gold bg-warning-bg'
                      : selectedFile
                        ? 'border-sand bg-cream-light'
                        : 'border-sand bg-white hover:border-sand-border'
                  } ${selectedFile ? 'p-4' : 'py-10 px-5'}`}
                  role="button"
                  tabIndex={0}
                  aria-label="Upload scorecard file"
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      fileInputRef.current?.click();
                    }
                  }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED_TYPES.join(',')}
                    onChange={handleInputChange}
                    className="hidden"
                  />

                  {selectedFile ? (
                    <div className="space-y-2">
                      {isPdfLoading ? (
                        <div
                          className="w-32 h-40 mx-auto bg-parchment rounded flex items-center justify-center"
                          role="status"
                        >
                          <div
                            className="animate-spin motion-reduce:animate-none w-6 h-6 border-2 border-gold border-t-transparent rounded-full"
                            aria-hidden="true"
                          />
                          <span className="sr-only">Loading PDF...</span>
                        </div>
                      ) : pdfPages.length > 0 ? (
                        <div className="space-y-3">
                          <div className="relative inline-block">
                            <img
                              src={pdfPages[currentPage]?.url}
                              alt={`Page ${currentPage + 1}`}
                              className="max-h-40 mx-auto rounded transition-transform duration-200"
                              style={{
                                transform: `rotate(${pageRotations[currentPage] || 0}deg)`,
                              }}
                            />
                          </div>
                          {pdfPages.length > 1 && (
                            <div className="flex items-center justify-center gap-2">
                              {pdfPages.map((page, idx) => (
                                <button
                                  key={idx}
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setCurrentPage(idx);
                                  }}
                                  className={`relative p-0.5 rounded transition-all ${
                                    idx === currentPage
                                      ? 'ring-2 ring-gold ring-offset-1'
                                      : 'hover:ring-2 hover:ring-sand'
                                  }`}
                                >
                                  <img
                                    src={page.url}
                                    alt={`Page ${idx + 1}`}
                                    className="w-12 h-16 object-cover rounded"
                                    style={{ transform: `rotate(${pageRotations[idx] || 0}deg)` }}
                                  />
                                  <span className="absolute bottom-0 left-0 right-0 text-[9px] bg-ink/60 text-linen text-center rounded-b">
                                    {idx + 1}
                                  </span>
                                </button>
                              ))}
                            </div>
                          )}
                          {pdfPages.length > 1 && (
                            <p className="text-[11px] font-sans text-warm-gray text-center">
                              Page {currentPage + 1} of {pdfPages.length}
                            </p>
                          )}
                        </div>
                      ) : previewUrl ? (
                        <div className="relative inline-block">
                          <img
                            src={previewUrl}
                            alt="Preview"
                            className="max-h-44 mx-auto rounded transition-transform duration-200"
                            style={{ transform: `rotate(${rotation}deg)` }}
                          />
                        </div>
                      ) : (
                        <div
                          className="w-24 h-32 mx-auto bg-parchment rounded flex items-center justify-center transition-transform duration-200"
                          style={{ transform: `rotate(${rotation}deg)` }}
                        >
                          <span className="font-sans text-[11px] text-warm-gray">
                            {selectedFile.type === 'application/pdf' ? 'PDF' : 'HEIC'}
                          </span>
                        </div>
                      )}

                      {/* Rotation controls */}
                      <div className="flex items-center justify-center gap-2 mt-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (pdfPages.length > 0) {
                              setPageRotations((prev) => {
                                const n = [...prev];
                                n[currentPage] = ((prev[currentPage] - 90 + 360) % 360) as
                                  | 0
                                  | 90
                                  | 180
                                  | 270;
                                return n;
                              });
                            } else {
                              setRotation(
                                (prev) => ((prev - 90 + 360) % 360) as 0 | 90 | 180 | 270
                              );
                            }
                          }}
                          className="p-1.5 text-warm-gray hover:text-ink hover:bg-parchment rounded transition-colors"
                          title="Rotate counter-clockwise"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M3 10h10a5 5 0 015 5v4M3 10l4-4M3 10l4 4"
                            />
                          </svg>
                        </button>
                        <span className="font-sans text-[10px] text-warm-gray min-w-[32px] text-center">
                          {pdfPages.length > 0 ? pageRotations[currentPage] || 0 : rotation}&deg;
                        </span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (pdfPages.length > 0) {
                              setPageRotations((prev) => {
                                const n = [...prev];
                                n[currentPage] = ((prev[currentPage] + 90) % 360) as
                                  | 0
                                  | 90
                                  | 180
                                  | 270;
                                return n;
                              });
                            } else {
                              setRotation((prev) => ((prev + 90) % 360) as 0 | 90 | 180 | 270);
                            }
                          }}
                          className="p-1.5 text-warm-gray hover:text-ink hover:bg-parchment rounded transition-colors"
                          title="Rotate clockwise"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M21 10h-10a5 5 0 00-5 5v4M21 10l-4-4M21 10l-4 4"
                            />
                          </svg>
                        </button>
                      </div>

                      {/* Orientation toggle */}
                      <div className="flex items-center justify-center gap-1 mt-1">
                        <span className="font-sans text-[10px] text-warm-gray mr-1">Output:</span>
                        {(['auto', 'landscape', 'portrait'] as const).map((o) => (
                          <button
                            key={o}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOrientation(o);
                            }}
                            className={`px-2 py-0.5 font-sans text-[10px] rounded transition-colors ${
                              orientation === o
                                ? 'bg-ink text-linen'
                                : 'bg-parchment text-espresso hover:bg-sand'
                            }`}
                          >
                            {o.charAt(0).toUpperCase() + o.slice(1)}
                          </button>
                        ))}
                      </div>

                      {/* File info + remove */}
                      <p className="font-sans text-[12px] text-espresso">
                        {selectedFile.name}
                        <span className="text-warm-gray ml-2">
                          ({(selectedFile.size / 1024 / 1024).toFixed(1)} MB)
                        </span>
                      </p>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFile(null);
                          setPreviewUrl(null);
                          setPdfPages([]);
                          setCurrentPage(0);
                          setRotation(0);
                          setPageRotations([]);
                          setOrientation('auto');
                        }}
                        className="font-sans text-[12px] text-error hover:underline"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div>
                      <div className="text-[36px] mb-2 opacity-50">&#128203;</div>
                      <p className="font-serif text-[15px] text-espresso mb-1.5">
                        Drop your scorecard here
                      </p>
                      <p className="font-sans text-[12px] text-warm-gray-light">
                        JPEG, PNG, WebP, HEIC, or PDF &middot; Max {MAX_FILE_SIZE_MB}MB
                      </p>
                    </div>
                  )}
                </div>

                {/* Game selector (hidden for contextual uploads) */}
                {gameContext && gameId ? (
                  <div className="bg-ink rounded p-3 flex justify-between items-center">
                    <div>
                      <div className="font-serif text-[15px] text-linen font-semibold">
                        {gameContext.awayTeam} @ {gameContext.homeTeam}
                      </div>
                      <div className="font-sans text-[11px] text-warm-gray-light">
                        {gameContext.date}
                      </div>
                    </div>
                  </div>
                ) : (
                  <GameSelector selectedGamePk={selectedGameId} onGameSelect={setSelectedGameId} />
                )}

                {/* MVP selector */}
                {selectedGameId && (
                  <div>
                    <label className="font-sans text-[11px] text-warm-gray tracking-[1px] uppercase block mb-1">
                      Game MVP
                    </label>
                    <MvpSelector
                      players={gamePlayers?.players ?? []}
                      awayTeamCode={gamePlayers?.away_team_code ?? ''}
                      homeTeamCode={gamePlayers?.home_team_code ?? ''}
                      selectedMlbId={mvpPlayerId}
                      onSelect={(mlbId, name) => {
                        setMvpPlayerId(mlbId);
                        setMvpPlayerName(name);
                      }}
                      isLoading={playersLoading}
                    />
                  </div>
                )}

                {/* Caption */}
                <div>
                  <label className="font-sans text-[11px] text-warm-gray tracking-[1px] uppercase block mb-1">
                    Caption
                  </label>
                  <textarea
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    maxLength={MAX_CAPTION_LENGTH}
                    rows={2}
                    placeholder="Tell the story of this game..."
                    className="w-full px-2.5 py-2 border border-sand-border rounded-[3px] font-serif text-[13px] text-ink bg-cream-light resize-vertical min-h-[60px] focus:outline-none focus:border-ink transition-colors"
                  />
                  <p className="font-sans text-[10px] text-warm-gray text-right mt-0.5">
                    {caption.length}/{MAX_CAPTION_LENGTH}
                  </p>
                </div>

                {/* Attended checkbox */}
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <div
                    onClick={(e) => {
                      e.preventDefault();
                      setAttendedInPerson(!attendedInPerson);
                    }}
                    className={`w-5 h-5 border-2 rounded-[3px] flex items-center justify-center cursor-pointer transition-colors ${
                      attendedInPerson
                        ? 'border-gold bg-gold text-cream'
                        : 'border-sand-border bg-transparent'
                    }`}
                  >
                    {attendedInPerson && (
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </div>
                  <span className="font-sans text-[13px] text-espresso">I was at this game</span>
                </label>

                {/* Public/private toggle */}
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <div
                    onClick={(e) => {
                      e.preventDefault();
                      setIsPublic(!isPublic);
                    }}
                    className={`w-5 h-5 border-2 rounded-[3px] flex items-center justify-center cursor-pointer transition-colors ${
                      isPublic
                        ? 'border-gold bg-gold text-cream'
                        : 'border-sand-border bg-transparent'
                    }`}
                  >
                    {isPublic && (
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </div>
                  <span className="font-sans text-[13px] text-espresso">
                    Make this scorecard public
                  </span>
                </label>

                {/* Next button */}
                <button
                  onClick={() => setCurrentStep('metadata')}
                  disabled={!selectedFile || !selectedGameId}
                  className="w-full bg-ink text-linen font-sans text-[14px] font-bold tracking-[1.5px] uppercase py-3.5 rounded transition-opacity disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90"
                >
                  Next
                </button>
              </div>

              {/* ===== STEP 2: Rooting + Metadata ===== */}
              {currentStep === 'metadata' && (
                <div className="space-y-5">
                  {/* Who are you rooting for? */}
                  {gamePlayers && (
                    <div>
                      <label className="font-sans text-[10px] tracking-[2px] text-gold-text uppercase font-bold block mb-2">
                        Who are you rooting for?
                      </label>
                      <div className="grid grid-cols-3 gap-2">
                        {rootingButton(
                          'away_fan',
                          getTeamName(gamePlayers.away_team_code) ||
                            gamePlayers.away_team_code.toUpperCase(),
                          gamePlayers.away_team_code
                        )}
                        {rootingButton('neutral', 'Neutral')}
                        {rootingButton(
                          'home_fan',
                          getTeamName(gamePlayers.home_team_code) ||
                            gamePlayers.home_team_code.toUpperCase(),
                          gamePlayers.home_team_code
                        )}
                      </div>
                      {!userProfile?.favorite_team_code && (
                        <p className="mt-2 font-sans text-[11px] text-gold-text">
                          Set a favorite team in your{' '}
                          <a href="/profile" className="underline hover:no-underline">
                            profile settings
                          </a>{' '}
                          to auto-select your team.
                        </p>
                      )}
                    </div>
                  )}

                  {/* Scoring metadata in cream-light background */}
                  <div className="bg-cream-light border border-sand rounded-[6px] p-4">
                    <div className="font-sans text-[10px] tracking-[2px] text-gold-text uppercase font-bold mb-3">
                      Scoring Details
                    </div>
                    <p className="font-sans text-[11px] text-warm-gray mb-3">
                      Pre-filled from your profile defaults. All fields are optional.
                    </p>
                    <ScorecardMetadataFields
                      values={metadataValues}
                      onChange={handleMetadataChange}
                    />
                  </div>

                  {/* Upload progress */}
                  {uploadMutation.isPending && (
                    <div
                      className="bg-warning-bg border border-sand rounded p-3 flex items-center gap-2"
                      role="status"
                    >
                      <div
                        className="animate-spin motion-reduce:animate-none w-5 h-5 border-2 border-gold border-t-transparent rounded-full"
                        aria-hidden="true"
                      />
                      <span className="font-sans text-[13px] text-espresso">
                        Uploading your scorecard...
                      </span>
                    </div>
                  )}

                  {/* Overwrite confirmation */}
                  {showOverwriteConfirm && (
                    <div className="bg-warning-bg border border-parchment rounded-[6px] p-4 mb-3">
                      <div className="flex gap-3 items-start">
                        {existingThumbnailUrl && (
                          <img
                            src={existingThumbnailUrl}
                            alt="Existing scorecard"
                            className="w-16 h-20 object-cover rounded border border-sand"
                          />
                        )}
                        <div className="flex-1">
                          <p className="font-sans text-[13px] text-espresso font-semibold mb-1">
                            You already have a scorecard for this game
                          </p>
                          <p className="font-sans text-[12px] text-warm-gray leading-[1.5]">
                            Uploading will replace your existing submission. This cannot be undone.
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <button
                          type="button"
                          onClick={handleOverwriteCancel}
                          aria-label="Cancel replacement and keep existing scorecard"
                          className="flex-1 bg-white border border-sand text-espresso font-sans text-[13px] font-semibold py-2.5 rounded hover:bg-cream-light transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleOverwriteConfirm}
                          disabled={uploadMutation.isPending}
                          aria-label="Replace existing scorecard with new upload — this cannot be undone"
                          className="flex-1 bg-ink text-linen font-sans text-[13px] font-bold py-2.5 rounded transition-opacity disabled:opacity-40 hover:opacity-90"
                        >
                          {uploadMutation.isPending ? 'Uploading...' : 'Replace Scorecard'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Navigation buttons */}
                  {!showOverwriteConfirm && (
                    <div className="flex gap-3">
                      <button
                        onClick={() => setCurrentStep('select')}
                        className="flex-1 bg-white border border-sand text-espresso font-sans text-[13px] font-semibold py-3 rounded hover:bg-cream-light transition-colors"
                      >
                        Back
                      </button>
                      <button
                        onClick={handleUpload}
                        disabled={uploadMutation.isPending || checkExistingMutation.isPending}
                        className="flex-1 bg-ink text-linen font-sans text-[14px] font-bold tracking-[1.5px] uppercase py-3 rounded transition-opacity disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90"
                      >
                        {checkExistingMutation.isPending
                          ? 'Checking...'
                          : uploadMutation.isPending
                            ? 'Uploading...'
                            : 'Add to the Archive'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Badge celebration overlay (shows on top of everything) */}
      {showBadgeCelebration && earnedBadges.length > 0 && (
        <BadgeCelebration
          badges={earnedBadges}
          onClose={handleBadgeCelebrationClose}
          username={userProfile?.username || ''}
        />
      )}
    </div>
  );
}

export default UploadScorecardModal;
